import React, {useState, useEffect, useRef, useMemo} from 'react';
import {
    Container,
    MaterialContext,
    Panel,
    Text,
} from '@wonderlandengine/react-ui/components';
import {Align, Justify, ReactUiBase} from '@wonderlandengine/react-ui';
import {Localization} from '../utils/Localization.js';
import {CreditsJsonData} from '../data/credits-data.js';
import {MenuPanels, UIModel} from '../models/UIModel.js';
import {colorSwatch} from './utils/colorSwatch.js';
import {parseColor} from './utils/utils.js';
import {
    Component,
    Material,
    Mesh,
    MeshAttribute,
    MeshComponent,
    Object3D,
    property,
    Texture,
    WonderlandEngine,
} from '@wonderlandengine/api';
import {UIColorTextured} from '../types/materials.js';
import {vec3} from 'gl-matrix';
import {ColoredImage} from './components/colored-image.js';
const tempPosLength = 32;
const tempPositions = new Float32Array(3 * tempPosLength);
export class LittleBlobAnimation extends Component {
    static TypeName = 'little-blob-animation';

    f = 0;
    private _orgPos = vec3.create();

    min = new Float32Array(3);
    max = new Float32Array(3);
    boundingBoxSet: boolean;

    start(): void {}

    update(dt: number) {
        this.f += dt;
        const amplitude = 0.05;
        const baseScale = 1.5 - 2 * amplitude;
        const offset = amplitude * Math.sin(this.f * 2 * Math.PI);
        const scale = baseScale + offset;
        this.object.setScalingLocal([scale, scale, scale]);

        if (!this.boundingBoxSet) {
            this.getBoundingBox(this.object, this.min, this.max);
            this.boundingBoxSet = true;
            this.object.getPositionLocal(this._orgPos);
        }

        const sx = baseScale + offset;
        const sy = baseScale - offset;

        this.object.setPositionLocal([
            this._orgPos[0] - this.min[0] * (1 - sx),
            this._orgPos[1] + this.min[1] * (1 - sy),
            this._orgPos[2],
        ]);
    }

    getBoundingBox(ref: Object3D, min: Float32Array, max: Float32Array) {
        const c = ref.findByNameDirect('mesh')[0];
        const m = c?.getComponent(MeshComponent);
        if (m && m.mesh) {
            this.calculateMeshBoundingBox(m.mesh, min, max);
        }
    }

    /** Get bounding box of a mesh */
    calculateMeshBoundingBox(m: Mesh, min: Float32Array, max: Float32Array) {
        if (m.vertexCount === 0) return;

        const pos = m.attribute(MeshAttribute.Position);

        pos.get(0, min);
        max.set(min);

        for (let i = 1; i < pos.length; i += tempPosLength) {
            const p = pos.get(i, tempPositions);

            const end = Math.min(tempPosLength, pos.length - i);
            for (let x = 0; x < end; ++x) {
                min[0] = Math.min(p[x * 3], min[0]);
                min[1] = Math.min(p[x * 3 + 1], min[1]);
                min[2] = Math.min(p[x * 3 + 2], min[2]);

                max[0] = Math.max(p[x * 3], max[0]);
                max[1] = Math.max(p[x * 3 + 1], max[1]);
                max[2] = Math.max(p[x * 3 + 2], max[2]);
            }
        }
    }
}

const LittleBlob = (props: {
    blobEyeMat: Material;
    blobTexture: Texture;
    blobColor: string;
}) => {
    const blobRef = useRef<Object3D>(null);

    useEffect(() => {
        if (blobRef.current != null) {
            const lba = blobRef.current.getComponent(LittleBlobAnimation);
            if (!lba) {
                blobRef.current.addComponent(LittleBlobAnimation);
            }
        }
    }, [props]);

    return (
        <Panel
            ref={blobRef}
            material={props.blobEyeMat}
            top={4}
            width={40}
            height={40}
            marginBottom={15}
            rounding={10}
            alignSelf={Align.Center}
        >
            <ColoredImage
                top={-4}
                src={props.blobTexture}
                color={props.blobColor}
                width={50}
                height={50}
                marginBottom={10}
                rounding={0}
                alignSelf={Align.Center}
            ></ColoredImage>
        </Panel>
    );
};

const App = (props: {comp: CreditsList}) => {
    function useMatGrad(mat, color) {
        return useMemo(() => {
            const bm = mat.clone();
            (bm as UIColorTextured).setColor(parseColor(color));
            return bm;
        }, [mat, color]);
    }

    const [credits] = useState(CreditsJsonData);

    const [isCreditActive, setIsCreditActive] = useState(
        UIModel.instance.isMenuActive(MenuPanels.Credits)
    );

    useEffect(() => {
        const handlePropertyChanged = (propertyName: string) => {
            if (propertyName === UIModel.ACTIVE_MENU_CHANGED) {
                setIsCreditActive(UIModel.instance.isMenuActive(MenuPanels.Credits));
            }
        };

        UIModel.instance.propertyChanged.add(handlePropertyChanged);

        // Cleanup listener on unmount
        return () => {
            UIModel.instance.propertyChanged.remove(handlePropertyChanged);
        };
    }, []);

    if (!isCreditActive) {
        return null;
    }

    return (
        <MaterialContext.Provider value={props.comp}>
            <Container
                width="100%"
                height="100%"
                justifyContent={Justify.FlexStart}
                alignContent={Align.Center}
                padding={20}
                marginBottom={20}
            >
                {/* <Panel ref={imageRef} width={100} height={100} margin={10}></Panel> */}

                <LittleBlob
                    blobEyeMat={props.comp.blobEyeMat}
                    blobTexture={props.comp.blobTexture}
                    blobColor="#3969CA"
                />

                {/* Title */}
                <Text
                    marginTop={20}
                    material={props.comp.TitleTextMaterial}
                    fontSize={32}
                    color={colorSwatch.SubTitle}
                    text={Localization.instance.get(credits.title)}
                    textAlign="center"
                />
                {/* Subtitle */}
                <Text
                    fontSize={22}
                    color={colorSwatch.TitleText}
                    text={Localization.instance.get(credits.subtitle)}
                    textAlign="center"
                    marginTop={5}
                    marginBottom={50}
                />

                {/* Credits List */}
                {credits.credits.map((item, index) => (
                    <Container
                        key={index}
                        justifyContent={Justify.Center}
                        alignContent={Align.Center}
                        margin={20}
                    >
                        <Text
                            fontSize={30}
                            color={colorSwatch.SubTitle}
                            text={Localization.instance.get(item.name)}
                            textAlign="center"
                            marginBottom={5}
                        />
                        <Text
                            color={colorSwatch.TitleText}
                            fontSize={22}
                            text={Localization.instance.get(item.role)}
                            textAlign="center"
                        />
                    </Container>
                ))}

                {/* Special Thanks Section */}
                <Text
                    material={props.comp.TitleTextMaterial}
                    fontSize={30}
                    color={colorSwatch.SubTitle}
                    text={Localization.instance.get(credits.specialThanks.heading)}
                    textAlign="center"
                    margin={30}
                />
                {credits.specialThanks.entries.map((entry, index) => (
                    <Text
                        key={index}
                        fontSize={22}
                        color={colorSwatch.TitleText}
                        text={Localization.instance.get(entry)}
                        textAlign="center"
                        margin={10}
                    />
                ))}
                <Container height={10}></Container>
                <LittleBlob
                    blobEyeMat={props.comp.blobEyeMat}
                    blobTexture={props.comp.blobTexture}
                    blobColor="#EC55C4"
                />

                <Text
                    marginTop={20}
                    material={props.comp.TitleTextMaterial}
                    fontSize={30}
                    color={colorSwatch.SubTitle}
                    text={Localization.instance.get(credits.specialThanks.footer)}
                    textAlign="center"
                />
            </Container>
        </MaterialContext.Provider>
    );
};

export class CreditsList extends ReactUiBase {
    static TypeName = 'credits-list';
    static InheritProperties = true;

    static onRegister(engine: WonderlandEngine): void {
        engine.registerComponent(LittleBlobAnimation);
    }

    @property.material({required: true})
    TitleTextMaterial!: Material;

    @property.material({required: true})
    blobMat!: Material;

    @property.texture()
    blobTexture: Texture;

    @property.material({required: true})
    blobEyeMat!: Material;

    render() {
        return <App comp={this} />;
    }

    override update(dt: number) {
        super.update();
    }
}
